import { useI18n } from '@affine/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '../../ui/button';
import { AuthInput } from './auth-input';
import { AuthPageContainer } from './auth-page-container';
import * as styles from './share.css';
import { emailRegex } from './utils';

export type EmailDeliveryStatus = 'sent' | 'pending' | 'failed';
const MAX_STATUS_READ_FAILURES = 3;

export const ChangeEmailPage = ({
  onChangeEmail: propsOnChangeEmail,
  onEmailDeliveryStatus,
  onEmailDeliveryFailed,
  onEmailDeliveryStatusUnavailable,
}: {
  onChangeEmail: (email: string) => Promise<EmailDeliveryStatus>;
  onEmailDeliveryStatus?: (email: string) => Promise<EmailDeliveryStatus>;
  onEmailDeliveryFailed?: () => void;
  onEmailDeliveryStatusUnavailable?: () => void;
  onOpenAffine: () => void;
}) => {
  const t = useI18n();
  const [deliveryStatus, setDeliveryStatus] = useState<
    'idle' | 'sending' | 'pending' | 'sent'
  >('idle');
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);
  const onContinue = useCallback(
    () =>
      void (async () => {
        if (inFlightRef.current || deliveryStatus !== 'idle') {
          return;
        }
        if (!emailRegex.test(email)) {
          setIsValidEmail(false);
          return;
        }
        setIsValidEmail(true);
        inFlightRef.current = true;
        setDeliveryStatus('sending');
        setLoading(true);

        try {
          const status = await propsOnChangeEmail(email);
          if (status === 'sent') {
            setDeliveryStatus('sent');
          } else if (status === 'pending') {
            setDeliveryStatus('pending');
          } else {
            setDeliveryStatus('idle');
            onEmailDeliveryFailed?.();
          }
        } catch (error) {
          // The caller owns the user-facing error notification. Always make
          // the form retryable when a transport failure escapes that callback.
          console.error('Failed to send change email verification', error);
          setDeliveryStatus('idle');
        } finally {
          inFlightRef.current = false;
          setLoading(false);
        }
      })(),
    [deliveryStatus, email, onEmailDeliveryFailed, propsOnChangeEmail]
  );

  useEffect(() => {
    if (deliveryStatus !== 'pending' || !onEmailDeliveryStatus) {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    let statusReadFailures = 0;
    const poll = async () => {
      try {
        const status = await onEmailDeliveryStatus(email);
        if (status === 'sent') {
          setDeliveryStatus('sent');
          return;
        }
        if (status === 'failed') {
          setDeliveryStatus('idle');
          onEmailDeliveryFailed?.();
          return;
        }
        statusReadFailures = 0;
      } catch {
        statusReadFailures += 1;
        if (statusReadFailures >= MAX_STATUS_READ_FAILURES) {
          setDeliveryStatus('idle');
          onEmailDeliveryStatusUnavailable?.();
          return;
        }
      }
      if (!cancelled) {
        timer = window.setTimeout(poll, 2_000);
      }
    };
    timer = window.setTimeout(poll, 2_000);
    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [
    deliveryStatus,
    email,
    onEmailDeliveryFailed,
    onEmailDeliveryStatus,
    onEmailDeliveryStatusUnavailable,
  ]);
  const onEmailChange = useCallback((value: string) => {
    setEmail(value);
  }, []);
  return (
    <AuthPageContainer
      title={t['com.affine.auth.change.email.page.title']()}
      subtitle={t['com.affine.auth.change.email.page.subtitle']()}
    >
      <>
        <AuthInput
          className={styles.input}
          label={t['com.affine.settings.email']()}
          placeholder={t['com.affine.auth.sign.email.placeholder']()}
          value={email}
          onChange={onEmailChange}
          error={!isValidEmail}
          errorHint={
            isValidEmail ? '' : t['com.affine.auth.sign.email.error']()
          }
          onEnter={onContinue}
          disabled={deliveryStatus !== 'idle'}
        />
        <Button
          variant="primary"
          size="large"
          onClick={onContinue}
          loading={loading}
          disabled={deliveryStatus !== 'idle'}
        >
          {deliveryStatus === 'sent'
            ? t['com.affine.auth.sent.verify.email.hint']()
            : deliveryStatus === 'pending'
              ? t['com.affine.auth.sending.verify.email']()
              : t['com.affine.auth.set.email.save']()}
        </Button>
      </>
    </AuthPageContainer>
  );
};
